import { useEffect, useRef } from 'react'

import Card, { CardProps } from '@/components/base/card'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'

import { fullscreenCardAnimation } from './animations'
import { cn } from '@/lib/utils'

export type FullscreenCardProps = CardProps &
  Partial<{
    isThankYouPage: boolean
    description: string
    card: {
      media: MediaElement
    }
  }>

const FullscreenCard = ({
  isThankYouPage = false,
  link,
  media,
  card,
  keywords,
  description,
  className,
  ...props
}: FullscreenCardProps) => {
  const mediaCard = useRef<HTMLImageElement | HTMLVideoElement>(null!)
  const background = useRef<HTMLImageElement | HTMLVideoElement>(null!)

  useEffect(() => {
    if (mediaCard.current instanceof HTMLImageElement) return

    let videoCanplay = false

    const video = mediaCard.current as HTMLVideoElement
    const videoBg = background.current as HTMLVideoElement

    const handleCanplay = () => {
      videoCanplay = true
    }

    const handleTimeupdate = () => {
      if (!videoCanplay) return
      videoBg.currentTime = video.currentTime
    }

    video?.addEventListener('timeupdate', handleTimeupdate)
    videoBg?.addEventListener('canplay', handleCanplay)

    return () => {
      videoBg?.removeEventListener('canplay', handleCanplay)
      video?.removeEventListener('timeupdate', handleTimeupdate)
    }
  }, [])

  return (
    <Motion animation={fullscreenCardAnimation}>
      {(motionRef) => (
        <Section
          ref={motionRef}
          className={cn('md:px-margin lg:px-0', className)}
          {...props}
        >
          <div
            className={cn(
              'hidden lg:absolute lg:left-0 lg:top-0 lg:flex lg:h-full lg:w-full',
              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/45'
            )}
          >
            <Media ref={background} fill fit="cover" {...media} />
          </div>
          <Grid className="w-full lg:w-screen">
            <div
              className={cn(
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-8 lg:row-start-3'
              )}
            >
              <Card
                ref={mediaCard}
                link={link}
                media={card?.media || media}
                keywords={keywords}
                isThankYouPage={isThankYouPage}
                description={description}
                className="lg:mx-auto lg:h-full"
                data-motion-card
              />
            </div>
          </Grid>
        </Section>
      )}
    </Motion>
  )
}

export default FullscreenCard
